
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { 
  getCourses, 
  getCourseTypes, 
  publishCourseChanges,
  getCoursesWithPendingChanges,
  getDraftCourses
} from "@/services/courseManagementService";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, RefreshCw, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Course, CourseType } from "@/types/course";
import { supabase } from "@/integrations/supabase/client";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const CoursesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [pendingCourses, setPendingCourses] = useState<Course[]>([]);
  const [draftCourses, setDraftCourses] = useState<Course[]>([]);
  const [courseTypes, setCourseTypes] = useState<CourseType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isPublishing, setIsPublishing] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [newCourse, setNewCourse] = useState<{
    title: string;
    description: string;
    type_id: string;
  }>({
    title: "",
    description: "",
    type_id: "",
  });

  useEffect(() => {
    fetchAllCourseData();
    fetchCourseTypes();
  }, []);
  
  const fetchAllCourseData = async () => {
    setIsLoading(true);
    try {
      // Fetch all courses
      const allCourses = await getCourses();
      setCourses(allCourses);
      
      // Fetch courses with pending changes
      const coursesWithChanges = await getCoursesWithPendingChanges();
      setPendingCourses(coursesWithChanges);
      
      // Fetch draft courses
      const drafts = await getDraftCourses();
      setDraftCourses(drafts);
    } catch (error) {
      console.error("Error fetching courses data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load courses. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    try {
      const types = await getCourseTypes();
      setCourseTypes(types || []);
    } catch (error) {
      console.error("Error fetching course types:", error);
    }
  };

  const handleCreateCourse = async () => {
    try {
      if (!newCourse.title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Course title is required.",
        });
        return;
      }

      const { data, error } = await supabase
        .from("courses")
        .insert([
          {
            title: newCourse.title,
            description: newCourse.description,
            type_id: newCourse.type_id || null,
            modules_count: 0,
            lessons_count: 0,
            status: 'draft',
            has_pending_changes: false
          }
        ])
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Course created successfully.",
      });
      
      setIsCreating(false);
      setNewCourse({ title: "", description: "", type_id: "" });
      fetchAllCourseData();
    } catch (error) {
      console.error("Error creating course:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create course. Please try again.",
      });
    }
  };

  const handlePublishCourse = async (courseId: string) => {
    setIsPublishing(courseId);
    try {
      const success = await publishCourseChanges(courseId);
      
      if (success) {
        toast({
          title: "Course Published",
          description: "Course content has been published to learners.",
          variant: "default"
        });
        // Refresh the courses list to show updated status
        fetchAllCourseData();
      } else {
        toast({
          title: "Publication Failed",
          description: "Failed to publish course content. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error publishing course:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while publishing. Please try again.",
      });
    } finally {
      setIsPublishing(null);
    }
  };
  
  const getStatusBadge = (course: Course) => {
    if (course.status === 'draft') {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Draft</Badge>;
    }
    
    if (course.has_pending_changes) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Pending Changes</Badge>;
    }
    
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Published</Badge>;
  };
  
  const getCoursesToDisplay = () => {
    switch (activeTab) {
      case "pending":
        return pendingCourses;
      case "draft":
        return draftCourses;
      case "all":
      default:
        return courses;
    }
  };

  return (
    <AdminLayout title="Course Management">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Courses</CardTitle>
            <CardDescription>
              Manage your courses. Click on a course to manage its modules and lessons.
            </CardDescription>
          </div>
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Course
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList>
              <TabsTrigger value="all">All Courses</TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-1">
                Pending Changes
                {pendingCourses.length > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 ml-1 text-xs font-semibold text-white bg-blue-500 rounded-full">
                    {pendingCourses.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="draft" className="flex items-center gap-1">
                Draft
                {draftCourses.length > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 ml-1 text-xs font-semibold text-white bg-amber-500 rounded-full">
                    {draftCourses.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading courses...</p>
            </div>
          ) : getCoursesToDisplay().length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {activeTab === "pending" ? "No courses with pending changes found." : 
                 activeTab === "draft" ? "No draft courses found." : 
                 "No courses found. Create your first course to get started."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Modules</TableHead>
                  <TableHead>Lessons</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getCoursesToDisplay().map((course) => (
                  <TableRow 
                    key={course.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/admin/courses/modules/${course.id}`)}
                  >
                    <TableCell className="font-medium">{course.title}</TableCell>
                    <TableCell className="max-w-xs truncate">{course.description || "—"}</TableCell>
                    <TableCell>{course.type_name || "—"}</TableCell>
                    <TableCell>{getStatusBadge(course)}</TableCell>
                    <TableCell>{course.modules_count}</TableCell>
                    <TableCell>{course.lessons_count}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/courses/edit/${course.id}`);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {(course.has_pending_changes || course.status === 'draft') && (
                          <Button
                            variant="outline"
                            size="icon"
                            className={course.has_pending_changes ? "border-blue-200 text-blue-700 hover:bg-blue-50" : ""}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePublishCourse(course.id);
                            }}
                            disabled={isPublishing === course.id}
                          >
                            {course.has_pending_changes ? (
                              <AlertTriangle className={`h-4 w-4 ${isPublishing === course.id ? 'animate-pulse' : ''}`} />
                            ) : (
                              <RefreshCw className={`h-4 w-4 ${isPublishing === course.id ? 'animate-spin' : ''}`} />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Course Dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Course</DialogTitle>
            <DialogDescription>
              Create a new course for your platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Course Title</Label>
              <Input
                id="title"
                placeholder="Enter course title"
                value={newCourse.title}
                onChange={(e) => 
                  setNewCourse({ ...newCourse, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Course Description</Label>
              <Textarea
                id="description"
                placeholder="Enter course description"
                value={newCourse.description}
                onChange={(e) => 
                  setNewCourse({ ...newCourse, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Course Type</Label>
              <select
                id="type"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={newCourse.type_id}
                onChange={(e) => 
                  setNewCourse({ ...newCourse, type_id: e.target.value })
                }
              >
                <option value="">Select a type</option>
                {courseTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCourse}>Create Course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CoursesListPage;
